import type { Card, CardPatch, Conflict, SettingPatch, CardEvent } from '@hmawiw/shared';
import { apiBase } from '../native/platform';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The server could not be reached at all, as opposed to answering with an error. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : 'network error');
    this.name = 'NetworkError';
  }
}

export interface PublicUser {
  id: string;
  username: string;
  displayName: string | null;
  role: 'admin' | 'user';
  mustChangePassword: boolean;
  email: string | null;
}

export interface BoardInfo {
  id: string;
  name: string;
  role: 'owner' | 'editor' | 'viewer';
}

export type LocaleDefault = 'auto' | 'en' | 'fr';
export type TimeFormat = 'auto' | '12h' | '24h';

/** The admin's defaults: names for the axes, and the language and clock for users who left theirs automatic. */
export interface LabelDefaults {
  columnLabels: (string | null)[];
  bandLabels: (string | null)[];
  locale: LocaleDefault;
  timeFormat: TimeFormat;
}

export interface SessionResponse {
  token: string;
  deviceId: string;
  user: PublicUser;
  boards: BoardInfo[];
  defaults: LabelDefaults;
  mustChangePassword: boolean;
}

export interface MeResponse {
  user: PublicUser;
  boards: BoardInfo[];
  defaults: LabelDefaults;
  deviceId: string;
}

export interface MetaResponse {
  appTitle: string;
  version: string;
  buildId: string;
  installId: string;
  setupDone: boolean;
  registrationOpen: boolean;
  /** How long a deleted card can still be restored. */
  tombstoneDays: number;
  push: { publicKey: string };
  apk: { size: number; mtime: number; versionCode: number | null; versionName: string | null; commit: string | null } | null;
}

export interface Credentials {
  username: string;
  password: string;
  deviceName: string;
}

export interface PushRequest {
  cards: CardPatch[];
  settings?: Record<string, SettingPatch>;
  events?: CardEvent[];
}

export interface PushResult {
  id: string;
  status: 'created' | 'applied' | 'merged' | 'unchanged' | 'stale' | 'rejected';
  reason?: string;
  seq?: number;
  card?: Card;
}

export interface PushResponse {
  results: PushResult[];
  settings: Record<string, { status: 'applied' | 'unchanged' | 'stale'; row: { json: string; clock: string } }>;
  events?: { id: string; status: 'created' | 'unchanged' }[];
  seq: number;
  installId: string;
}

export interface RemoteConflict extends Conflict {
  dismissedAt: number | null;
}

export interface PullResponse {
  cards: Card[];
  conflicts: RemoteConflict[];
  settings: { key: string; json: string; clock: string }[];
  events?: CardEvent[];
  seq: number;
  hasMore: boolean;
  installId: string;
}

export interface DeviceInfo {
  id: string;
  name: string;
  createdAt: number;
  lastSeenAt: number | null;
  current: boolean;
}

async function request<T>(method: string, path: string, body?: unknown, token?: string | null): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${path}`, {
      method,
      headers: {
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    throw new NetworkError(e);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }
  if (!res.ok) {
    const err = (parsed as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'HTTP_' + res.status, err?.message ?? res.statusText, err?.details);
  }
  return parsed as T;
}

export const api = {
  meta: () => request<MetaResponse>('GET', '/api/meta'),
  setup: (c: Credentials) => request<SessionResponse>('POST', '/api/setup', c),
  login: (c: Credentials) => request<SessionResponse>('POST', '/api/login', c),
  register: (c: Credentials & { inviteToken?: string }) => request<SessionResponse>('POST', '/api/register', c),
  logout: (token: string) => request<void>('POST', '/api/logout', undefined, token),
  me: (token: string) => request<MeResponse>('GET', '/api/me', undefined, token),
  createBoard: (token: string, name: string) => request<{ board: BoardInfo; boards: BoardInfo[] }>('POST', '/api/boards', { name }, token),
  renameBoard: (token: string, id: string, name: string) => request<{ boards: BoardInfo[] }>('PATCH', `/api/boards/${id}`, { name }, token),
  deleteBoard: (token: string, id: string) => request<{ boards: BoardInfo[] }>('DELETE', `/api/boards/${id}`, undefined, token),
  changePassword: (token: string, next: string, current?: string) => request<void>('POST', '/api/me/password', { current, next }, token),
  setEmail: (token: string, email: string | null) => request<{ user: PublicUser }>('PATCH', '/api/me/email', { email }, token),
  forgotPassword: (username: string) => request<void>('POST', '/api/password/forgot', { username }),
  resetPassword: (token: string, password: string) => request<void>('POST', '/api/password/reset', { token, password }),
  devices: (token: string) => request<{ devices: DeviceInfo[] }>('GET', '/api/devices', undefined, token),
  revokeDevice: (token: string, id: string) => request<void>('DELETE', `/api/devices/${id}`, undefined, token),
  push: (token: string, body: PushRequest) => request<PushResponse>('POST', '/api/sync/push', body, token),
  pull: (token: string, since: number, limit = 500) => request<PullResponse>('GET', `/api/sync/pull?since=${since}&limit=${limit}`, undefined, token),
  dismissConflict: (token: string, id: string) => request<void>('POST', `/api/conflicts/${id}/dismiss`, undefined, token),
  pushSubscribe: (token: string, body: PushSubscriptionBody) => request<{ id: string; createdAt: number }>('PUT', '/api/push/subscription', body, token),
  pushSubscription: (token: string) => request<{ subscription: PushSubscriptionView | null }>('GET', '/api/push/subscription', undefined, token),
  pushUnsubscribe: (token: string) => request<void>('DELETE', '/api/push/subscription', undefined, token),
  pushTest: (token: string) => request<{ status: number }>('POST', '/api/push/test', undefined, token),
  admin: {
    users: (token: string) => request<{ users: AdminUser[] }>('GET', '/api/admin/users', undefined, token),
    createUser: (token: string, body: { username: string; role?: 'admin' | 'user'; temporaryPassword?: string }) =>
      request<{ user: AdminUser; temporaryPassword: string }>('POST', '/api/admin/users', body, token),
    patchUser: (token: string, id: string, body: { role?: 'admin' | 'user'; disabled?: boolean; temporaryPassword?: string; email?: string | null }) =>
      request<{ user: AdminUser; temporaryPassword?: string }>('PATCH', `/api/admin/users/${id}`, body, token),
    invites: (token: string) => request<{ invites: AdminInvite[] }>('GET', '/api/admin/invites', undefined, token),
    createInvite: (token: string, expiresInDays?: number) => request<{ id: string; url: string; expiresAt: number }>('POST', '/api/admin/invites', { expiresInDays }, token),
    revokeInvite: (token: string, id: string) => request<void>('DELETE', `/api/admin/invites/${id}`, undefined, token),
    settings: (token: string) => request<AdminSettings>('GET', '/api/admin/settings', undefined, token),
    saveSettings: (token: string, patch: Partial<AdminSettings>) => request<AdminSettings>('PUT', '/api/admin/settings', patch, token),
    stats: (token: string) => request<AdminStats>('GET', '/api/admin/stats', undefined, token),
  },
};

export interface AdminUser {
  id: string;
  username: string;
  role: 'admin' | 'user';
  createdAt: number;
  lastLoginAt: number | null;
  disabledAt: number | null;
  mustChangePassword: boolean;
  devices: number;
  email: string | null;
}

export interface AdminInvite {
  id: string;
  createdAt: number;
  expiresAt: number;
  usedAt: number | null;
  usedBy: string | null;
  revokedAt: number | null;
}

export interface AdminSettings {
  appTitle: string;
  registrationOpen: boolean;
  inviteExpiryDays: number;
  tombstoneDays: number;
  defaultColumnLabels: (string | null)[];
  defaultBandLabels: (string | null)[];
  apkDownloadEnabled: boolean;
  defaultLocale: LocaleDefault;
  defaultTimeFormat: TimeFormat;
}

export interface AdminStats {
  users: number;
  openCards: number;
  dbBytes: number | null;
  seq: number;
  lastPurgeAt: number | null;
  version: string;
  buildId: string;
}

export interface PushSubscriptionBody {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  timeZone: string;
  lang?: string;
}

export interface PushSubscriptionView {
  id: string;
  endpointHost: string;
  timeZone: string;
  createdAt: number;
  lastOkAt: number | null;
}
